/**
 * Weekly offer popup for Aromas de Brisa (`sections/ab-weekly-offer.liquid`).
 *
 * Opens the offer dialog automatically a few seconds after the visitor arrives, and once
 * more a while after they close it (up to `data-max-shows` automatic openings per browsing
 * session, counted across pages). The floating tab reopens it at any time.
 */

const STORAGE_KEY = 'ab-weekly-offer';

/** @returns {{ shows: number, closedAt: number }} */
function readState() {
  try {
    return { shows: 0, closedAt: 0, ...JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') };
  } catch {
    return { shows: 0, closedAt: 0 };
  }
}

/** @param {{ shows: number, closedAt: number }} state */
function saveState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Without storage the popup still works, it just restarts its count on each page.
  }
}

class AbWeeklyOffer extends HTMLElement {
  #timer = 0;
  #controller = new AbortController();
  /** @type {{ shows: number, closedAt: number }} */
  #state = { shows: 0, closedAt: 0 };

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.dialog = this.querySelector('dialog');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.firstDelay = (Number(this.dataset.firstDelay) || 2) * 1000;
    this.repeatDelay = (Number(this.dataset.repeatDelay) || 20) * 1000;
    this.maxShows = Number(this.dataset.maxShows) || 2;
    this.#state = readState();

    for (const opener of this.querySelectorAll('[data-ab-weekly-open]')) {
      opener.addEventListener('click', () => this.open(false), { signal });
    }
    for (const closer of this.querySelectorAll('[data-ab-weekly-close]')) {
      closer.addEventListener('click', () => this.dialog.close(), { signal });
    }
    // A click on the backdrop (the dialog element itself) closes it.
    this.dialog.addEventListener('click', (event) => event.target === this.dialog && this.dialog.close(), { signal });
    this.dialog.addEventListener('close', () => this.#onClose(), { signal });

    if (this.#state.shows === 0) {
      this.#schedule(this.firstDelay);
    } else if (this.#state.shows < this.maxShows && this.#state.closedAt) {
      this.#schedule(this.repeatDelay - (Date.now() - this.#state.closedAt));
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearTimeout(this.#timer);
  }

  /** @param {boolean} automatic Automatic openings count towards `maxShows`. */
  open(automatic = true) {
    if (!this.dialog || this.dialog.open) return;

    // Never cover another open dialog (cart, order form): try again a bit later.
    if (automatic && document.querySelector('dialog[open]')) {
      this.#schedule(3000);
      return;
    }

    if (automatic) {
      this.#state.shows += 1;
      saveState(this.#state);
    }
    this.dialog.showModal();
  }

  #onClose() {
    this.#state.closedAt = Date.now();
    saveState(this.#state);
    if (this.#state.shows < this.maxShows) this.#schedule(this.repeatDelay);
  }

  /** @param {number} delay */
  #schedule(delay) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.open(true), Math.max(1000, delay));
  }
}

if (!customElements.get('ab-weekly-offer')) customElements.define('ab-weekly-offer', AbWeeklyOffer);
